import type { ViewCommand } from './types';

/*
 * Map a semantic `view_command` to an SPA route. Commands are intentionally
 * semantic (not routes); unknown types/pages are ignored (return null). Param key
 * names follow the backend contract with a few fallbacks since the frame shape
 * is not described in the OpenAPI spec.
 */

const pick = (cmd: ViewCommand, ...keys: string[]): string | undefined => {
  for (const k of keys) {
    const v = cmd[k];
    if (v != null && v !== '') return String(v);
  }
  return undefined;
};

export interface ResolvedViewCommand {
  href: string;
  label: string;
}

export function resolveViewCommand(
  cmd: ViewCommand | null | undefined
): ResolvedViewCommand | null {
  if (!cmd || !cmd.type) return null;
  const label = cmd.label ? String(cmd.label) : undefined;

  if (cmd.type === 'navigate') {
    const url = cmd.url;
    // The backend validates the path/query against its own route allowlist;
    // this is defense in depth against an absolute URL, a scheme-relative
    // "//host" URL, or a backslash reaching router.push. Tab/LF/CR are
    // rejected too: the WHATWG URL parser strips them, so "/\t/evil.com/"
    // would otherwise resolve to "//evil.com/" once it reaches router.push.
    if (
      typeof url !== 'string' ||
      !url.startsWith('/') ||
      url.startsWith('//') ||
      /[\\\t\r\n]/.test(url)
    ) {
      return null;
    }
    return { href: url, label: label ?? 'Open' };
  }

  // Deprecated types: the assistant no longer emits these (replaced by `navigate`
  // above), but older running backends might still — keep handling them until
  // every deployment has switched over.
  switch (cmd.type) {
    case 'open_company': {
      const id = pick(
        cmd,
        'company_external_id',
        'company_id',
        'company',
        'external_id'
      );
      if (!id) return null;
      // next.config has trailingSlash:true — keep the trailing slash so
      // router.push navigates directly instead of hitting a redirect.
      return {
        href: `/companies/${encodeURIComponent(id)}/`,
        label: label ?? 'Open company',
      };
    }
    case 'open_compliance_check': {
      const company = pick(cmd, 'company_external_id', 'company_id', 'company');
      if (!company) return null;
      const result = pick(cmd, 'result_external_id', 'result');
      const query = result ? `?result=${encodeURIComponent(result)}` : '';
      return {
        href: `/companies/${encodeURIComponent(company)}/compliance/${query}`,
        label: label ?? 'Open compliance check',
      };
    }
    case 'open_corporate_linkage': {
      const company = pick(cmd, 'company_external_id', 'company_id', 'company');
      if (!company) return null;
      return {
        href: `/companies/${encodeURIComponent(company)}/linkage/`,
        label: label ?? 'Open corporate structure',
      };
    }
    case 'open_article': {
      const id = pick(
        cmd,
        'article_external_id',
        'article_id',
        'article',
        'external_id'
      );
      if (!id) return null;
      return {
        href: `/articles/${encodeURIComponent(id)}/`,
        label: label ?? 'Open article',
      };
    }
    case 'open_registration': {
      // Missing-company investigation creates a placeholder registration; the
      // existing registration page shows it (no dedicated investigation page).
      const id = pick(
        cmd,
        'registration_external_id',
        'registration_id',
        'registration',
        'external_id'
      );
      if (!id) return null;
      return {
        href: `/companies/registration/?registration_id=${encodeURIComponent(id)}`,
        label: label ?? 'Open company',
      };
    }
    default:
      // Unknown command type — ignore.
      return null;
  }
}
